import {
  ClipBoxTool,
  Quaternion,
  Vector3,
  Web3DEventName,
  Web3DViewer,
} from '@technology/web3d';
import { Accessor, createEffect, createSignal, Setter } from 'solid-js';
import { CapabilityCategory } from './base/models/capability-category';
import { SingleCapabilityBase } from './base/single-capability-base';

/**
 * Wraps the engine's clip-box around the viewer camera when active so
 * the visible point cloud is constrained while in a panorama/planar
 * pane. The clip distance is reactive — moving the per-pane Clip
 * Distance slider (or any upstream that feeds the supplied accessor)
 * updates the clip box live.
 *
 * Distance doubles as the on/off control: distance == 0 deactivates the
 * capability (no clip box); the upstream coordinator hides the loaded
 * cloud via root.visible rather than unloading it. Distance > 0
 * activates and applies the clip box.
 */
export class ImagePointcloudCapability extends SingleCapabilityBase {
  public override category: CapabilityCategory = CapabilityCategory.Command;

  private readonly clipBoxTool: ClipBoxTool;
  private readonly position: Accessor<Vector3>;
  private readonly setPosition: Setter<Vector3>;

  private readonly positionChanged = (): void => {
    const position = this.position();

    if (!position.equals(this.viewer.camera.position)) {
      this.setPosition(this.viewer.camera.position.clone());
    }
  };

  constructor(viewer: Web3DViewer, clipDistance: Accessor<number>) {
    super(viewer);

    const [position, setPosition] = createSignal<Vector3>(new Vector3());
    this.position = position;
    this.setPosition = setPosition;

    this.clipBoxTool = this.viewer.tools.get(ClipBoxTool)!;

    createEffect(() => {
      if (!this.isEnabled()) return;
      const distance = clipDistance();
      if (distance > 0) {
        if (!this.isActive()) this.activate();
      } else if (this.isActive()) {
        this.deactivate();
      }
    });

    createEffect(() => {
      const active = this.isActive();

      if (active) {
        this.viewer.addEventListener(
          'navigationend' as Web3DEventName.NavigationEnd,
          this.positionChanged
        );
      } else {
        this.viewer.removeEventListener(
          'navigationend' as Web3DEventName.NavigationEnd,
          this.positionChanged
        );
      }
    });

    createEffect(() => {
      const active = this.isActive();
      const distance = clipDistance();
      const shouldApply = active && distance > 0;
      this.clipBoxTool.clear();
      this.clipBoxTool.controlsVisible = !shouldApply;

      if (shouldApply) {
        const prevActiveTool = this.viewer.activeTool ?? '';
        this.viewer.activeTool = 'clipBox';

        const position = this.position();

        const size = new Vector3(1, 1, 1);
        size.multiplyScalar(distance);

        this.clipBoxTool.add(position, new Quaternion(), size);

        this.viewer.activeTool = prevActiveTool;
      }
    });
  }

  public async cleanup(): Promise<void> {
    this.clipBoxTool.clear();
    // Restore visibility so the shared clipBoxTool isn't left hidden if the effect
    // was disposed before disable(). Assume true is the default; revisit if used elsewhere.
    this.clipBoxTool.controlsVisible = true;
    await Promise.resolve();
  }
}
